// client/src/services/wqmsService.js
import axios from "axios";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: 15000,
});

// Attach token automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle unauthorized access
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// ---------- helpers ----------
function pickArrayShape(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.rows)) return raw.rows;
  if (Array.isArray(raw?.stations)) return raw.stations;
  return [];
}

function buildParams({ timeRange, at, from, to, fallback }) {
  const params = new URLSearchParams();
  const hasWindow = !!(from || to || at);

  if (!hasWindow && timeRange) params.set("timeRange", timeRange);
  if (at) params.set("at", at);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (fallback !== undefined) params.set("fallback", String(fallback));

  return params.toString();
}

// Canonical station normalizer -> always [{ id, name, ...rest }]
export function normalizeStations(raw) {
  const arr = pickArrayShape(raw);
  return arr.map((s, i) => {
    const idRaw =
      s.id ?? s.station_id ?? s.stationId ?? (i + 1);
    const nameRaw =
      s.name ??
      s.site_name ??
      s.station_name ??
      (s.district ? `Station ${idRaw} — ${s.district}` : `Station ${idRaw}`);
    return { id: String(idRaw), name: String(nameRaw), ...s };
  });
}

// one cache shared by all pages
let _stationsCache = null;

/** Returns normalized stations: [{ id, name, ... }] */
async function getStations(opts = {}) {
  // ← accept old usage getStations("10")
  if (typeof opts === "string") opts = { timeRange: opts };

  const {
    timeRange = "60",
    at, from, to, fallback,
    force = false,
    signal,
    side, river, drain,
  } = opts;

  const hasWindow = !!(from || to || at);
  const hasFilter  = (side !== "All") || (river !== "All") || (drain !== "All");

  // Only use cache when no explicit time window AND no filters
   if (!force && !hasWindow && !hasFilter && _stationsCache) return _stationsCache;

  const qs = new URLSearchParams();
  if (!hasWindow && timeRange) qs.set("timeRange", timeRange);
  if (at)   qs.set("at", at);
  if (from) qs.set("from", from);
  if (to)   qs.set("to", to);
  if (fallback !== undefined) qs.set("fallback", String(fallback));

  // Send filters only when not "All" to keep URLs clean
if (side)  qs.set("side", side);
 if (river) qs.set("river", river);
 if (drain) qs.set("drain", drain);

  const res  = await api.get(`/wqms/stations?${qs.toString()}`, { signal });
  const list = normalizeStations(res.data);

  //if (!hasWindow) _stationsCache = list;
  // Cache only the generic “All” result
  if (!hasWindow && !hasFilter) _stationsCache = list;
  return list;
}


const wqmsService = {
  // --- STATIONS LIST (canonical) ---
 getStations,

  /* ================== SINGLE STATION ================== */
  async getStationData(
    stationId,
    { timeRange = "60", at, from, to, fallback, signal } = {}
  ) {
    const qs = buildParams({ timeRange, at, from, to, fallback });
    const res = await api.get(`/wqms/station/${stationId}?${qs}`, { signal });
    return res.data;
  },

  /* ================== OVERALL STATS ================== */
  async getStats(timeRange = "60", at, from, to) {
    const qs = buildParams({ timeRange, at, from, to });
    const res = await api.get(`/wqms/stats?${qs}`);
    return res.data;
  },

  /* ================== TREND DATA ================== */
  async getTrendData(stationId, timeRange = "60", at, from, to, opts = {}) {
  const { signal } = opts || {};
  const qs = buildParams({ timeRange, at, from, to });
  const res = await api.get(`/wqms/station/${stationId}/trend?${qs}`, { signal });
  return res.data;
},

  /* ============== REPORTS: MONTHLY WQI ============== */
  async getMonthlyWqi(stationId, year, month) {
    const res = await api.get("/reports/monthly-wqi", {
      params: { stationId, year, month },
    });
    return res.data;
  },

  /* ============== 2-Y TIME PLOT: COMPARE API ============== */
  async getTimeseriesCompare({
    stationId,
    params,
    start,
    end,
    res: resolution,
    signal,
  } = {}) {
    const qs = new URLSearchParams();
    if (stationId != null) qs.set("stationId", String(stationId));
    if (params) qs.set("params", params);
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    if (resolution) qs.set("res", resolution);

    const r = await api.get(`/timeseries/compare?${qs.toString()}`, { signal });
    return r.data;
  },

  /* ================== EXPORT CSV ================== */
  async exportCSV(stationId, timeRange = "60", at, from, to) {
    const qs = buildParams({ timeRange, at, from, to });
    const res = await api.get(`/wqms/station/${stationId}/export?${qs}`);
    const history = Array.isArray(res.data?.data) ? res.data.data : [];
    const station = res.data?.station || {};

    if (!history.length) {
      //alert("No data found for the selected time range.");
      return;
    }

    const formatLocal = (ts) =>
      ts
        ? new Date(ts).toLocaleString("en-GB", {
            timeZone: "Asia/Karachi",
            hour12: true,
          })
        : "";

    const fmtNum = (v, d = 2) => {
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(d) : "";
    };

    const esc = (val) => {
      const s = String(val ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const COLUMNS = [
      { key: "timestamp", label: "Timestamp", fmt: (v) => formatLocal(v) },
      { key: "ammonia_mg_l_avg",          alt: "ammonia_mg_l_clean_avg",      label: "Ammonia (mg/L)",            dec: 3 },
      { key: "bod_mg_l_avg",              alt: "bod_mg_l_clean_avg",          label: "BOD (mg/L)",                dec: 1 },
      { key: "cod_mg_l_avg",              alt: "cod_mg_l_clean_avg",          label: "COD (mg/L)",                dec: 1 },
      { key: "chloride_mg_l_avg",         alt: "chloride_mg_l_clean_avg",     label: "Chloride (mg/L)",           dec: 1 },
      { key: "color_hazen_avg",           alt: "color_hazen_clean_avg",       label: "Color (Hazen)",             dec: 1 },
      { key: "dissolved_oxygen_mg_l_avg", alt: "dissolved_oxygen_mg_l_clean_avg", label: "Dissolved Oxygen (mg/L)", dec: 1 },
      { key: "toc_mg_l_avg",              alt: "toc_mg_l_clean_avg",          label: "TOC (mg/L)",                dec: 1 },
      { key: "doc_mg_l_avg",              alt: "doc_mg_l_clean_avg",          label: "DOC (mg/L)",                dec: 1 },
      { key: "ph_avg",                    alt: "ph_clean_avg",                label: "pH",                        dec: 2 },
      { key: "redox_mv_avg",              alt: "redox_mv_clean_avg",          label: "ORP (mV)",                  dec: 0 },
      { key: "temperature_c_avg",         alt: "temperature_c_clean_avg",     label: "Temperature (°C)",          dec: 1 },
      { key: "tss_mg_l_avg",              alt: "tss_mg_l_clean_avg",          label: "TSS (mg/L)",                dec: 1 },
      { key: "turbidity_ntu_avg",         alt: "turbidity_ntu_clean_avg",     label: "Turbidity (NTU)",           dec: 1 },
      { key: "level_m_avg",               alt: "level_m_clean_avg",           label: "Water Level (m)",           dec: 2 },
      { key: "conductivity_us_cm_avg",    alt: "conductivity_us_cm_clean_avg",label: "Conductivity (µS/cm)",      dec: 1 },
    ];

    const stationName = station.site_name || `Station ${stationId}`;
    const location = station.district || "";
    const timeWindow =
      from && to
        ? `${formatLocal(from)} → ${formatLocal(to)}`
        : `Last ${timeRange} minutes (PKT)`;

    const headerLines = [
      `WQMS-${stationId} — Data Report`,
      `Site,${esc(stationName)}`,
      `Location,${esc(location)}`,
      `Time range,${esc(timeWindow)}`,
      "",
    ];

    const tableHeader = COLUMNS.map((c) => esc(c.label)).join(",");
    const rows = history.map((row) => {
      const cells = COLUMNS.map((c) => {
        const raw = c.key in row ? row[c.key] : c.alt ? row[c.alt] : undefined;
        return c.fmt ? esc(c.fmt(raw)) : esc(fmtNum(raw, c.dec ?? 2));
      });
      return cells.join(",");
    });

    const csv = [...headerLines, tableHeader, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${stationName.replace(/\s+/g, "_")}_Report_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /* ================== EXPORT PDF ================== */
  async exportPDF(stationId, timeRange = "60", at, from, to) {
    const qs = buildParams({ timeRange, at, from, to });
    const res = await api.get(`/wqms/station/${stationId}/export-pdf?${qs}`);
    const payload = res.data || {};
    const station = payload.station || {};
    const history = Array.isArray(payload.history) ? payload.history : [];

    if (!history.length) {
      alert("No data found for the selected time range.");
      return;
    }

    const formatLocal = (ts) =>
      ts
        ? new Date(ts).toLocaleString("en-GB", {
            timeZone: "Asia/Karachi",
            hour12: true,
          })
        : "";

    const fmtNum = (v, d = 2) => {
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(d) : "—";
    };

    const COLUMNS = [
      { key: "timestamp", label: "Timestamp", align: "left", format: (v) => formatLocal(v) },
      { key: "ammonia_mg_l_avg", label: "Ammonia (mg/L)", align: "center", decimals: 3 },
      { key: "bod_mg_l_avg", label: "BOD (mg/L)", align: "center", decimals: 1 },
      { key: "cod_mg_l_avg", label: "COD (mg/L)", align: "center", decimals: 1 },
      { key: "chloride_mg_l_avg", label: "Chloride (mg/L)", align: "center", decimals: 1 },
      { key: "color_hazen_avg", label: "Color (Hazen)", align: "center", decimals: 1 },
      { key: "dissolved_oxygen_mg_l_avg", label: "Dissolved Oxygen (mg/L)", align: "center", decimals: 1 },
      { key: "toc_mg_l_avg", label: "TOC (mg/L)", align: "center", decimals: 1 },
      { key: "doc_mg_l_avg", label: "DOC (mg/L)", align: "center", decimals: 1 },
      { key: "ph_avg", label: "pH", align: "center", decimals: 2 },
      { key: "redox_mv_avg", label: "ORP (mV)", align: "center", decimals: 0 },
      { key: "temperature_c_avg", label: "Temperature (°C)", align: "center", decimals: 1 },
      { key: "tss_mg_l_avg", label: "TSS (mg/L)", align: "center", decimals: 1 },
      { key: "turbidity_ntu_avg", label: "Turbidity (NTU)", align: "center", decimals: 1 },
      { key: "level_m_avg", label: "Water Level (m)", align: "center", decimals: 2 },
      { key: "conductivity_us_cm_avg", label: "Conductivity (µS/cm)", align: "center", decimals: 1 },
    ];

    const thead = `<thead><tr>${COLUMNS.map(
      (c) => `<th class="${c.align || "center"}">${c.label}</th>`
    ).join("")}</tr></thead>`;
    const tbody = `
      <tbody>
        ${history
          .map(
            (row) => `
          <tr>
            ${COLUMNS.map((c) => {
              const val = c.format
                ? c.format(row[c.key])
                : fmtNum(row[c.key], c.decimals ?? 2);
              return `<td class="${c.align || "center"}">${val}</td>`;
            }).join("")}
          </tr>`
          )
          .join("")}
      </tbody>`;

    const fromLocal = formatLocal(from);
    const toLocal = formatLocal(to);

    const html = `
      <div style="padding:20px;font-family:'Segoe UI',Arial,sans-serif;">
        <h1 style="text-align:center;color:#555;margin-bottom:4px;">
          ${station.site_name || "Station"} — Data Report
        </h1>
        <h3 style="text-align:center;margin-top:0;color:#ccc;">${
          station.district || ""
        }</h3>
        <p style="text-align:center;font-size:12px;color:#ccc;margin-top:2px;">
          Time range: ${
            fromLocal && toLocal
              ? `${fromLocal} → ${toLocal}`
              : `Last ${timeRange} minutes (PKT)`
          }
        </p>
        <table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:12px;">
          ${thead}
          ${tbody}
        </table>
        <div style="text-align:right;font-size:10px;color:#888;margin-top:10px;">
          © Pakistan WQI • Generated ${formatLocal(Date.now())}
        </div>
      </div>
      <style>
        table { border-collapse: collapse; width: 100%; }
        thead th {
          background: #555; color: white; font-weight: 600;
          padding: 6px 8px; border: 1px solid #f1f5f9; text-align: center;
        }
        tbody td { padding: 5px 6px; border: 1px solid #e2e8f0; text-align: center; }
        tbody tr:nth-child(even) { background: #fafafa; }
        td.left, th.left { text-align: left; }
      </style>
    `;

    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-9999px";
    tempDiv.innerHTML = html;
    document.body.appendChild(tempDiv);

    const canvas = await html2canvas(tempDiv, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

    const fileName = `${(station.site_name || "Station")
      .replace(/\s+/g, "_")}_Report_${new Date().toISOString().split("T")[0]}.pdf`;
    pdf.save(fileName);

    document.body.removeChild(tempDiv);
  },

  /* ================== EXPORT RAW RECORDS ================== */
  async exportRecords(stationId, timeRange = "60", at, from, to) {
    const qs = buildParams({ timeRange, at, from, to });
    const res = await api.get(`/wqms/station/${stationId}/export?${qs}`);
    return res.data;
  },

  /* ================== LAST RECORD ================== */
  async getLastRecord(stationId, { signal } = {}) {
    const res = await api.get(`/wqms/station/${stationId}/last-record`, {
      signal,
    });
    return res.data; // { success: true, data: { last_record_utc: "...Z" } }
  },

async getSeriesMultiStations({ stationIds, param, start, end, res, signal } = {}) {
  const qs = new URLSearchParams();
  if (stationIds?.length) qs.set("stationIds", stationIds.join(","));
  if (param) qs.set("param", param);
  if (start) qs.set("start", start);
  if (end) qs.set("end", end);
  if (res) qs.set("res", res);

  const r = await api.get(`/timeseries_multi/series?${qs.toString()}`, { signal });
  return r.data; // [{ts, station_id, value}]
},
      /* ================== REGULATORY EXCEEDANCE SUMMARY ================== */
  /**
   * Fetch exceedance summary report
   * @param {Object} opts
   * @param {string} opts.start - Start date (YYYY-MM-DD)
   * @param {string} opts.end - End date (YYYY-MM-DD)
   * @param {string} [opts.res] - Resolution: daily | weekly | monthly
   * @param {string|number} [opts.stationId] - Optional station filter
   */
  async getExceedanceReport({ start, end, res = "daily", stationId, signal } = {}) {
    const qs = new URLSearchParams();
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    if (res) qs.set("res", res);
    if (stationId) qs.set("stationId", String(stationId));

    const response = await api.get(`/wqms/reports/exceedance?${qs.toString()}`, { signal });
    return Array.isArray(response.data) ? response.data : [];
  },

  /**
   * Export exceedance report as CSV
   * (optional helper if you need programmatic download instead of frontend table)
   */
  async exportExceedanceCSV({ start, end, res = "daily", stationId }) {
    const qs = new URLSearchParams();
    if (start) qs.set("start", start);
    if (end) qs.set("end", end);
    if (res) qs.set("res", res);
    if (stationId) qs.set("stationId", String(stationId));

    const resObj = await api.get(`/wqms/reports/exceedance?${qs.toString()}`);
    const rows = Array.isArray(resObj.data) ? resObj.data : [];

    if (!rows.length) {
      alert("No exceedance data found for selected range.");
      return;
    }

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(",")),
    ].join("\n");

    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ExceedanceReport_${start}_to_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export default wqmsService;
